import type { CSVValidationError, ParsedCSVRow, CSVParseResult } from '../types/asset-import.types';

// Required columns matching backend ImportTemplateProvider.cs
// These are the Label values from the backend template
const REQUIRED_COLUMNS = [
  'Asset Code',
  'Asset Name',
  'Category',
  'Location',
  'Acquisition Cost',
  'Acquisition Date'
];

// Optional columns - comprehensive list matching backend template
const OPTIONAL_COLUMNS = [
  // Basic Information
  'Sub Category',
  'Status',
  'Currency',
  'Quantity',
  'Serial Number',
  'Manufacturer',
  'Model',
  'Barcode',
  'Barcode Type',
  'Barcode Value',
  'Resource Code',
  'Sub Ledger',
  'Registration Number',
  'Description',
  // Financial Values (for opening balances)
  'Net Book Value',
  'Accumulated Depreciation',
  'Accumulated Additions',
  'Accumulated Disposals',
  'Accumulated Revaluation',
  'NBV at Start of Year',
  'NBV at End of Year',
  // Assignment
  'Assigned To',
  'Assigned Date',
  // Depreciation Settings
  'Depreciation Method',
  'Depreciation Percentage',
  'Useful Life (Years)',
  'Residual Value',
  'Posting Frequency',
  'Do Not Depreciate',
  'In Service Period',
  'In Service Year',
  'Base Value On',
  // Nominal Accounts
  'Asset Cost Nominal',
  'Depreciation BS Nominal',
  'Depreciation PL Nominal',
  'Disposals Nominal',
  'Revaluation BS Nominal',
  'Revaluation PL Nominal',
  'Disposals Bank Account Nominal',
  'Additions WIP Bank Account Nominal',
  // Costing
  'Project',
  'Cost Centre',
  // Supplier Information
  'Supplier Name',
  'Supplier Code',
  'Purchase Order Number',
  'Invoice Reference',
  'Hire Purchase',
  // Insurance Information
  'Insurance Policy Number',
  'Insurance Provider',
  'Insurance Start Date',
  'Insurance Expiry Date',
  'Insurance Value',
  'Insurance Notes',
  // Warranty Information
  'Warranty Provider',
  'Warranty Start Date',
  'Warranty Expiry Date',
  'Warranty Terms',
  'Warranty Notes',
  // Custom User Fields
  'User Key 1',
  'User Key 2',
  'User Key 3',
  'User Key 4',
  'User Key 5',
  'User Value 1',
  'User Value 2',
  'User Value 3',
  'User Value 4',
  'User Value 5',
  'User Date 1',
  'User Date 2',
  'User Date 3',
  // Notes
  'Notes'
];

export const parseCSV = (file: File): Promise<CSVParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          resolve({ data: [], errors: [{ row: 0, field: 'File', error: 'File is empty' }] });
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/(^")|("$)/g, ''));
        const errors: CSVValidationError[] = [];

        // Validate required columns
        REQUIRED_COLUMNS.forEach((column) => {
          if (!headers.includes(column)) {
            errors.push({
              row: 0,
              field: column,
              error: `Missing required column: ${column}`
            });
          }
        });

        if (errors.length > 0) {
          resolve({ data: [], errors });
          return;
        }

        // Parse data rows
        const data: ParsedCSVRow[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/(^")|("$)/g, ''));
          const row: ParsedCSVRow = {};
          
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });

          data.push(row);
        }

        resolve({ data, errors: [] });
      } catch (error) {
        reject(new Error(`CSV parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

export const generateCSVTemplate = (): string => {
  const headers = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

  // Escape CSV values that contain commas or quotes
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  // Create CSV string with headers only (no sample data)
  const headerRow = headers.map(escapeCSV).join(',');

  return headerRow + '\n';
};

export const downloadCSVTemplate = () => {
  const csv = generateCSVTemplate();
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'asset-import-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
